import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { apiClient } from "../lib/apiClient";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { NotificationBell } from "../components/NotificationBell";
import { ThemeToggle } from "../components/ThemeToggle";
import { HelpDialog } from "../components/HelpDialog";
import {
  Heart,
  Users,
  MapPin,
  LogOut,
  ArrowLeft,
  Church,
  User,
  Shield,
  ShieldCheck,
  Search,
  X,
  HelpCircle,
} from "lucide-react";


export default function ChurchesPage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [churches, setChurches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchChurches = useCallback(async () => {
    try {
      const res = await apiClient.get("churches");
      setChurches(res.data || []);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("auth_token");
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) setUser(JSON.parse(userData));
    fetchChurches();
  }, [fetchChurches]);

  const handleLogout = async () => {
    try { await apiClient.post("authLogout"); } catch { /* ignore */ }
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };

  const isAdmin = user?.is_admin || (user?.church_admin_ids?.length > 0);
  const isGlobalAdmin = user?.is_admin;

  const filteredChurches = churches.filter((c) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return c.name.toLowerCase().includes(q) || (c.address || "").toLowerCase().includes(q);
  });

  const isChurchAdminOf = (churchId) => isGlobalAdmin || (user?.church_admin_ids || []).includes(churchId);

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-light flex items-center justify-center">
        <div className="animate-pulse text-stone-dark/50 font-serif">Loading churches...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      {/* Nav */}
      <nav className="bg-white border-b border-stone px-4 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="text-stone-dark/60 hover:text-sage" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
              <Heart className="w-4 h-4 text-white" />
            </div>
            <span className="font-serif font-semibold text-stone-dark">Churches</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="rounded-full" data-testid="user-menu">
                  <User className="w-4 h-4 mr-2" /> {user?.name}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem className="text-stone-dark/70">{user?.email}</DropdownMenuItem>
                {isGlobalAdmin && (
                  <DropdownMenuItem className="text-sage">
                    <Shield className="w-4 h-4 mr-2" /> Administrator
                  </DropdownMenuItem>
                )}
                {!isGlobalAdmin && (user?.church_admin_ids?.length > 0) && (
                  <DropdownMenuItem className="text-sage">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Church Admin ({user.church_admin_ids.length})
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setHelpOpen(true)} data-testid="help-btn">
                  <HelpCircle className="w-4 h-4 mr-2" /> Help &amp; Support
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-stone-dark mb-2" data-testid="churches-title">
              Churches
            </h1>
            <p className="text-stone-dark/60">Browse every church and the prayer groups that meet within it.</p>
          </div>
          {isAdmin && (
            <Link to="/admin?tab=churches" data-testid="manage-churches-link">
              <Button variant="outline" className="rounded-full border-sage text-sage hover:bg-sage/10">
                <Shield className="w-4 h-4 mr-2" />
                Manage churches
              </Button>
            </Link>
          )}
        </div>

        {/* Search */}
        {churches.length > 0 && (
          <div className="relative max-w-md mb-6">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-dark/40" />
            <Input
              placeholder="Search churches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 bg-white"
              data-testid="search-churches"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-dark/40 hover:text-stone-dark"
                aria-label="clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {/* Church directory — each card shows the church + its groups inline */}
        {filteredChurches.length === 0 ? (
          <Card className="border-stone border-dashed">
            <CardContent className="p-12 text-center">
              <Church className="w-16 h-16 text-stone-dark/15 mx-auto mb-4" />
              <p className="text-stone-dark/60 text-lg font-serif">
                {searchQuery ? "No churches match your search" : "No churches yet"}
              </p>
              <p className="text-sm text-stone-dark/40 mt-2">
                {!isAdmin && !searchQuery
                  ? "An administrator will add churches soon."
                  : searchQuery
                  ? "Try adjusting your search terms."
                  : "Open the Admin panel to add the first church."}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-5">
            {filteredChurches.map((church) => {
              const canManage = isChurchAdminOf(church.id);
              const groups = church.groups || [];
              return (
                <Card
                  key={church.id}
                  className="border-stone card-hover"
                  data-testid={`church-card-${church.id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex items-start gap-3 min-w-0 flex-1">
                        <div className="w-10 h-10 rounded-xl bg-sage/10 flex items-center justify-center shrink-0">
                          <Church className="w-5 h-5 text-sage" />
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-serif font-semibold text-stone-dark leading-tight">{church.name}</h3>
                          {church.address && (
                            <p className="text-sm text-stone-dark/60 flex items-start gap-1.5 mt-1">
                              <MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                              <span className="truncate">{church.address}</span>
                            </p>
                          )}
                        </div>
                      </div>
                      {canManage && (
                        <span
                          className="text-xs font-medium px-2 py-0.5 rounded-full bg-sage/10 text-sage shrink-0"
                          data-testid={`church-admin-badge-${church.id}`}
                        >
                          {isGlobalAdmin ? "Admin" : "Church Admin"}
                        </span>
                      )}
                    </div>

                    {/* Groups in this church */}
                    <div className="mt-4 pt-4 border-t border-stone/60">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-stone-dark/60 uppercase tracking-wide flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5" />
                          {church.group_count} group{church.group_count !== 1 ? "s" : ""}
                        </span>
                      </div>
                      {groups.length === 0 ? (
                        <p className="text-sm text-stone-dark/40 italic">No groups yet at this church.</p>
                      ) : (
                        <ul className="space-y-1.5" data-testid={`church-${church.id}-groups`}>
                          {groups.map((g) => (
                            <li key={g.id}>
                              <Link
                                to={`/groups/${g.id}`}
                                className={`flex items-center justify-between text-sm rounded-lg px-2.5 py-1.5 hover:bg-sage/5 transition-colors ${
                                  g.is_disabled ? "opacity-50" : ""
                                }`}
                                data-testid={`church-group-link-${g.id}`}
                              >
                                <span className="text-stone-dark/80 font-medium truncate">
                                  {g.name}
                                  {g.is_disabled && <span className="ml-2 text-xs text-stone-dark/40">(disabled)</span>}
                                </span>
                                <span className="text-xs text-stone-dark/50 shrink-0">
                                  {g.member_count} member{g.member_count !== 1 ? "s" : ""}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>

      <HelpDialog open={helpOpen} onOpenChange={setHelpOpen} />
    </div>
  );
}
